import { normalizeMergeTagKey } from './mergeTagDictionary';

/**
 * Bounded-box sizing for dynamic image fields.
 *
 * A dynamic field is filled at send time by a real per-record asset whose shape
 * nobody controls. The template therefore fixes a box, and the fit rule is a
 * property of WHICH field it is — never a per-block user choice:
 *
 *   logo   → contain. A brand mark must never be sliced.
 *   photo  → cover, center-cropped. Fills the box so every record looks alike.
 *   circle → cover, then a circular mask; width and height must stay equal.
 *
 * Keys are normalized through the merge-tag dictionary first, so aliases
 * (user.avatar, listing.photo, …) classify the same as their canonical key.
 */

export type DynamicImageFieldKind = 'logo' | 'photo' | 'circle';
export type DynamicImageFit = 'contain' | 'cover';

const LOGO_FIELD_KEYS = new Set([
	'user.logo',
	'user.logoDarkUrl',
	'user.logoLightUrl',
	'user.officeLogoDarkUrl',
	'user.officeLogoLightUrl',
	'listing.officeLogoDark',
	'listing.officeLogoLight',
]);

const CIRCLE_FIELD_KEYS = new Set(['user.profileImageCircle']);

const PHOTO_FIELD_KEYS = new Set([
	'listing.image_data_0',
	'listing.image_data_1',
	'listing.image_data_2',
	'listing.image_data_3',
	'listing.image_data_4',
	'listing.image_data_5',
	'listing.image_data_6',
	'listing.image_data_7',
	'listing.image_data_8',
	'listing.image_data_9',
	'listing.imagePath',
	'listing.thumbPath',
	'listing.agentImagePath',
	'listing.secondAgentImagePath',
	'user.profileImagePortrait',
	'user.profileImageSquare',
]);

/** Strips `{{ }}` and any raw-block wrapper, leaving the bare dotted key. */
export function normalizeDynamicImageTag(tag?: string | null): string {
	return String(tag || '')
		.replace(/\{\{\{\{\/?raw\}\}\}\}/gi, '')
		.replace(/^\{\{|\}\}$/g, '')
		.trim();
}

export function getDynamicImageFieldKind(
	tag?: string | null
): DynamicImageFieldKind | null {
	const key = normalizeDynamicImageTag(tag);
	if (!key) return null;

	const canonical = normalizeMergeTagKey(key);
	if (CIRCLE_FIELD_KEYS.has(canonical)) return 'circle';
	if (LOGO_FIELD_KEYS.has(canonical)) return 'logo';
	if (PHOTO_FIELD_KEYS.has(canonical)) return 'photo';
	return null;
}

export function getDynamicImageFit(
	kind: DynamicImageFieldKind | null
): DynamicImageFit | null {
	if (!kind) return null;
	return kind === 'logo' ? 'contain' : 'cover';
}

/**
 * Cover crops to fill the box, so it needs both numbers. Contain only needs a
 * width — the other side could scale.
 *
 * NOTE: product has since required that EVERY dynamic field carry explicit
 * dimensions for its whole lifecycle, so callers currently treat all three
 * kinds as explicit. Kept as the fit-level truth in case that relaxes.
 */
export function dynamicImageRequiresExplicitHeight(
	kind: DynamicImageFieldKind | null
): boolean {
	return kind === 'photo' || kind === 'circle';
}

/**
 * Shape to assume for a dynamic field that has no asset to measure yet.
 *
 * A field is given explicit dimensions the moment it is created, before any
 * fallback exists, so the height has to come from somewhere — and until a real
 * asset arrives, the thing actually sitting in that box is the shared dynamic
 * image placeholder. It is square, so the box is square: the placeholder fills
 * it exactly instead of being letterboxed or cropped inside a wider frame.
 *
 * One ratio for every field kind, deliberately. This replaced a per-field table
 * (logo 3:1, photo 3:2, portrait 3:4) whose numbers described what a *real*
 * asset of that kind usually looks like — which is not what is on screen at this
 * point, and was superseded the moment a fallback was uploaded anyway.
 *
 * Sourced from the placeholder asset rather than guessed. If that art is ever
 * re-exported at a different aspect, this constant is what moves — the asset
 * follows the box, not the other way round, so the change is reviewable in a
 * diff instead of silently resizing every dynamic image created afterward.
 *
 * @see dynamic-image-placeholder.png (square) in `blocks/standard/Image`
 */
export const DYNAMIC_IMAGE_DEFAULT_ASPECT = 1;
